import { useId, useState } from 'react';
import type { CreateVariantInput } from '../api/catalog-api.js';

const UNIT_OPTIONS = ['g', 'kg', 'ml', 'l', 'pcs'] as const;

export interface VariantFormProps {
  initial?: Partial<CreateVariantInput>;
  onSubmit: (input: CreateVariantInput) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  /** Once a variant has any inventory movement, its SKU becomes immutable server-side (see docs/catalog-architecture.md "SKU rules") -- the field is disabled here rather than letting the user submit a change the backend will reject anyway. */
  skuLocked?: boolean;
  submitLabel?: string;
}

/** Money fields are entered in rupees (a human-friendly unit) and converted to integer paise on submit -- `Math.round` only, never a float carried through state or sent to the backend. */
export function VariantForm({ initial, onSubmit, onCancel, isSubmitting, skuLocked, submitLabel = 'Save variant' }: VariantFormProps) {
  const [sku, setSku] = useState(initial?.sku ?? '');
  const [variantName, setVariantName] = useState(initial?.variantName ?? '');
  const [unitOfMeasure, setUnitOfMeasure] = useState(initial?.unitOfMeasure ?? 'pcs');
  const [netQuantity, setNetQuantity] = useState(initial?.netQuantity ?? '1');
  const [mrpRupees, setMrpRupees] = useState(initial?.mrpPaise ? String(initial.mrpPaise / 100) : '');
  const [sellingRupees, setSellingRupees] = useState(
    initial?.sellingPricePaise ? String(initial.sellingPricePaise / 100) : '',
  );
  const [barcode, setBarcode] = useState(initial?.barcode ?? '');
  const [weightGrams, setWeightGrams] = useState(initial?.weightGrams ? String(initial.weightGrams) : '');
  const [lengthMm, setLengthMm] = useState(initial?.lengthMm ? String(initial.lengthMm) : '');
  const [widthMm, setWidthMm] = useState(initial?.widthMm ? String(initial.widthMm) : '');
  const [heightMm, setHeightMm] = useState(initial?.heightMm ? String(initial.heightMm) : '');
  const [error, setError] = useState<string | null>(null);

  const skuId = useId();
  const nameId = useId();
  const barcodeId = useId();
  const weightId = useId();
  const lengthId = useId();
  const widthId = useId();
  const heightId = useId();

  /** Blank -> `undefined` (field simply not sent); a value that fails to parse as a positive integer -> `null` so the caller can reject it, rather than silently sending `NaN`. */
  function parseOptionalPositiveInt(raw: string): number | null | undefined {
    if (!raw.trim()) return undefined;
    const value = Number(raw);
    if (!Number.isFinite(value) || !Number.isInteger(value) || value <= 0) return null;
    return value;
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const mrpPaise = Math.round(Number(mrpRupees) * 100);
    const sellingPricePaise = Math.round(Number(sellingRupees) * 100);
    if (!variantName.trim()) {
      setError('Variant name is required.');
      return;
    }
    if (!Number.isFinite(mrpPaise) || mrpPaise < 0 || !Number.isFinite(sellingPricePaise) || sellingPricePaise < 0) {
      setError('Enter valid, non-negative prices.');
      return;
    }
    if (sellingPricePaise > mrpPaise) {
      setError('Selling price cannot exceed MRP.');
      return;
    }
    const weight = parseOptionalPositiveInt(weightGrams);
    const length = parseOptionalPositiveInt(lengthMm);
    const width = parseOptionalPositiveInt(widthMm);
    const height = parseOptionalPositiveInt(heightMm);
    if (weight === null || length === null || width === null || height === null) {
      setError('Weight and dimensions must be positive whole numbers.');
      return;
    }
    setError(null);
    onSubmit({
      sku: sku.trim() || undefined,
      variantName: variantName.trim(),
      unitOfMeasure,
      netQuantity,
      mrpPaise,
      sellingPricePaise,
      barcode: barcode.trim() || undefined,
      weightGrams: weight,
      lengthMm: length,
      widthMm: width,
      heightMm: height,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-xl border border-gray-100 bg-gray-50 p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={nameId} className="mb-1 block text-xs font-medium text-gray-600">
            Variant name
          </label>
          <input
            id={nameId}
            value={variantName}
            onChange={(event) => setVariantName(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
        <div>
          <label htmlFor={skuId} className="mb-1 block text-xs font-medium text-gray-600">
            SKU <span className="font-normal text-gray-400">(leave blank to auto-generate)</span>
          </label>
          <input
            id={skuId}
            value={sku}
            onChange={(event) => setSku(event.target.value)}
            disabled={skuLocked}
            title={skuLocked ? 'This variant has inventory history; its SKU can no longer change.' : undefined}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 disabled:bg-gray-100 disabled:text-gray-400"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">Unit</label>
          <select
            value={unitOfMeasure}
            onChange={(event) => setUnitOfMeasure(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          >
            {UNIT_OPTIONS.map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">Net quantity</label>
          <input
            value={netQuantity}
            onChange={(event) => setNetQuantity(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">MRP (₹)</label>
          <input
            inputMode="decimal"
            value={mrpRupees}
            onChange={(event) => setMrpRupees(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">Selling price (₹)</label>
          <input
            inputMode="decimal"
            value={sellingRupees}
            onChange={(event) => setSellingRupees(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <label htmlFor={barcodeId} className="mb-1 block text-xs font-medium text-gray-600">
            Barcode <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            id={barcodeId}
            value={barcode}
            onChange={(event) => setBarcode(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
        <div>
          <label htmlFor={weightId} className="mb-1 block text-xs font-medium text-gray-600">
            Weight (g)
          </label>
          <input
            id={weightId}
            inputMode="numeric"
            value={weightGrams}
            onChange={(event) => setWeightGrams(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
        <div>
          <label htmlFor={lengthId} className="mb-1 block text-xs font-medium text-gray-600">
            Length (mm)
          </label>
          <input
            id={lengthId}
            inputMode="numeric"
            value={lengthMm}
            onChange={(event) => setLengthMm(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <label htmlFor={widthId} className="mb-1 block text-xs font-medium text-gray-600">
              Width (mm)
            </label>
            <input
              id={widthId}
              inputMode="numeric"
              value={widthMm}
              onChange={(event) => setWidthMm(event.target.value)}
              className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </div>
          <div className="flex-1">
            <label htmlFor={heightId} className="mb-1 block text-xs font-medium text-gray-600">
              Height (mm)
            </label>
            <input
              id={heightId}
              inputMode="numeric"
              value={heightMm}
              onChange={(event) => setHeightMm(event.target.value)}
              className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </div>
        </div>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-brand text-brand-foreground rounded-full px-4 py-1.5 text-sm font-semibold disabled:opacity-60"
        >
          {isSubmitting ? 'Saving…' : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full border border-gray-200 px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
